import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImages } from "@fortawesome/free-solid-svg-icons";
import { fetchSitelineEntries, sitelineLink } from "../api/sitelineFeed";
import type { SitelineEntry } from "../types";
import SitelineCard from "./SitelineCard";
import SitelineTodayStrip from "./SitelineTodayStrip";

const LAST_SEEN_KEY = "siteline:lastSeenTs";

type Phase = "loading" | "ready" | "empty";

function Skeletons({ n = 5 }: { n?: number }) {
  return (
    <>
      {Array.from({ length: n }).map((_, i) => (
        <div className="skel-card" key={i}>
          <div className="skel-row">
            <div className="shim skel-emoji" />
            <div className="shim skel-line" style={{ width: 110 }} />
            <div className="shim skel-line" style={{ width: 44, marginLeft: "auto" }} />
          </div>
          <div className="shim skel-line" style={{ width: "85%", height: 16, marginBottom: 9 }} />
          <div className="shim skel-line" style={{ width: "62%" }} />
          {i % 3 === 0 && <div className="shim" style={{ width: "100%", height: 130, borderRadius: 6, marginTop: 12 }} />}
        </div>
      ))}
    </>
  );
}

function CaughtUp({ bottom }: { bottom?: boolean }) {
  return (
    <div className={"caughtup" + (bottom ? " bottom" : "")}>
      {bottom ? "that's the whole timeline" : <>you're all caught up <span className="cu-spark">✦</span></>}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="empty">
      <div className="empty-mark">✦</div>
      <h3>Nothing's happened yet</h3>
      <p>The moment someone pulls a card, rates a coffee, or the next drop lands — it'll show up right here.</p>
    </div>
  );
}

const SitelineFeed: React.FC = () => {
  const [entries, setEntries] = useState<SitelineEntry[]>([]);
  const [phase, setPhase] = useState<Phase>("loading");
  const [pending, setPending] = useState<SitelineEntry[]>([]);
  const [fresh, setFresh] = useState<Set<string>>(() => new Set());
  const [readTs, setReadTs] = useState<number>(() => Number(localStorage.getItem(LAST_SEEN_KEY)) || 0);
  const navigate = useNavigate();

  const knownIds = useRef<Set<string>>(new Set());
  const pendingRef = useRef<SitelineEntry[]>([]);
  useEffect(() => { pendingRef.current = pending; }, [pending]);

  // initial load
  useEffect(() => {
    let alive = true;
    (async () => {
      const data = await fetchSitelineEntries().catch(() => [] as SitelineEntry[]);
      if (!alive) return;
      knownIds.current = new Set(data.map((e) => e.id));
      setEntries(data);
      setPhase(data.length ? "ready" : "empty");
      // mark caught-up shortly after viewing so the boundary advances next visit
      if (data.length) {
        const newest = data[0].occurredAt;
        setTimeout(() => {
          if (alive) localStorage.setItem(LAST_SEEN_KEY, String(newest));
        }, 4000);
      }
    })();
    return () => { alive = false; };
  }, []);

  // live poll → surface genuinely-new entries in the "N new" pill
  useEffect(() => {
    if (phase !== "ready") return undefined;
    const iv = setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      const data = await fetchSitelineEntries().catch(() => null);
      if (!data) return;
      const newOnes = data.filter((e) => !knownIds.current.has(e.id));
      if (!newOnes.length) return;
      newOnes.forEach((e) => knownIds.current.add(e.id));
      setPending((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...newOnes.filter((e) => !seen.has(e.id)), ...prev];
      });
    }, 60000);
    return () => clearInterval(iv);
  }, [phase]);

  const flushPending = useCallback(() => {
    const p = pendingRef.current;
    if (!p.length) return;
    const ids = p.map((e) => e.id);
    setEntries((cur) => {
      const have = new Set(cur.map((c) => c.id));
      return [...p.filter((e) => !have.has(e.id)), ...cur].sort((a, b) => b.occurredAt - a.occurredAt);
    });
    setFresh(new Set(ids));
    setPending([]);
    pendingRef.current = [];
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    setTimeout(() => setFresh((f) => { const n = new Set(f); ids.forEach((i) => n.delete(i)); return n; }), 1900);
    const newest = Math.max(...p.map((e) => e.occurredAt), readTs);
    setReadTs(Date.now());
    localStorage.setItem(LAST_SEEN_KEY, String(newest));
  }, [readTs]);

  const openItem = useCallback((e: SitelineEntry) => {
    const to = sitelineLink(e);
    if (to) navigate(to);
  }, [navigate]);

  // split into new (above last-read) / seen (below)
  const splitIdx = (() => {
    if (!readTs) return entries.length;
    const i = entries.findIndex((e) => e.occurredAt <= readTs);
    return i === -1 ? entries.length : i;
  })();
  const newItems = entries.slice(0, splitIdx);
  const seenItems = entries.slice(splitIdx);

  return (
    <>
      <header className="siteline-mast">
        <div className="siteline-mast-inner">
          <div className="mast-title">siteline</div>
          <div className="mast-sub"><span className="live-dot" /> live</div>
          <button
            className="mast-gallery"
            onClick={() => navigate("/art-of-the-day/gallery")}
            aria-label="Art of the Day gallery"
            title="Art of the Day gallery"
          >
            <FontAwesomeIcon icon={faImages} />
          </button>
        </div>
      </header>

      {phase === "ready" && <SitelineTodayStrip entries={entries} />}

      <div className="feed-col">
        {phase === "loading" && <Skeletons n={5} />}
        {phase === "empty" && <EmptyState />}
        {phase === "ready" && (
          <>
            {newItems.map((e) => (
              <SitelineCard key={e.id} e={e} onOpen={openItem} fresh={fresh.has(e.id)} />
            ))}
            {(seenItems.length > 0 || newItems.length === 0) && <CaughtUp />}
            {seenItems.map((e) => (
              <SitelineCard key={e.id} e={e} onOpen={openItem} fresh={fresh.has(e.id)} />
            ))}
            <CaughtUp bottom />
          </>
        )}
      </div>

      <div className={"newpill" + (pending.length ? " show" : "")} onClick={flushPending}>
        <span className="uparrow">↑</span>
        <span className="npc">{pending.length}</span>
        new {pending.length === 1 ? "update" : "updates"}
      </div>
    </>
  );
};

export default SitelineFeed;
